import React, { useState, useEffect, useRef } from 'react'
import { OmButton, OmSpinner, OmSwitch, omMessage } from '@owma/kit'
import { ReactSortable } from 'react-sortablejs'

import mailboxApiHelper from 'services/mailbox-api'
import { store as reduxStore } from 'index'

import i18n from 'i18n'
import classNames from 'classnames'

import { confirmBase, confirmDelete } from 'utils/ui'

import { getMenuIcon } from 'pages/main/data/IconHelper'
import Avatar from 'pages/calendar/components/Avatar'
import dragHandleSvg from 'assets/images/button-drag-handle-24.svg'

import { openEditServerRule } from './Editor'
import styles from './style.module.scss'

export interface FilterCondition {
  test: 'always' | 'from' | 'to' | 'cc' | 'subject' | 'body' | 'subjectOrBody' | 'over' | 'under'
  value: string
  option?: 'all' | 'domain'
  not?: boolean
}

export interface FilterAction {
  action: 'move' | 'copy' | 'delete' | 'markRead' | 'forward' | 'redirect' | 'markJunk'
  arg?: string
}

export interface FilterDto {
  id: number
  order?: number
  name?: string
  rule: { conditions: FilterCondition[]; actions: FilterAction[]; stop?: boolean }
  enabled?: boolean
}

const conditionTestKeyMap: Record<string, string> = {
  from: 'serverRules.condition.from',
  to: 'serverRules.condition.to',
  cc: 'serverRules.condition.cc',
  subject: 'serverRules.condition.subject',
  body: 'serverRules.condition.body',
  subjectOrBody: 'serverRules.condition.subjectOrBody',
  over: 'serverRules.condition.over',
  under: 'serverRules.condition.under',
}

const actionKeyMap: Record<string, string> = {
  move: 'serverRules.action.move',
  copy: 'serverRules.action.copy',
  delete: 'serverRules.action.delete',
  markRead: 'serverRules.action.markRead',
  forward: 'serverRules.action.forward',
  redirect: 'serverRules.action.redirect',
  markJunk: 'serverRules.action.markJunk',
}

function buildSummary(filter: FilterDto): string {
  const parts: string[] = []

  if (filter.rule?.conditions) {
    for (const cond of filter.rule.conditions) {
      if (cond.test === 'always') continue
      /* i18next-extract-disable-next-line */
      const label = i18n.t(conditionTestKeyMap[cond.test] || cond.test)
      parts.push(`${label}: ${cond.value}`)
    }
  }

  if (filter.rule?.actions) {
    for (const act of filter.rule.actions) {
      /* i18next-extract-disable-next-line */
      const label = i18n.t(actionKeyMap[act.action] || act.action)
      if (act.arg) {
        const displayArg = ['move', 'copy'].includes(act.action)
          ? act.arg.split('/').pop()
          : act.arg
        parts.push(`${label}: ${displayArg}`)
      } else {
        parts.push(label)
      }
    }
  }

  return parts.join(', ')
}

const ServerRulesList: React.FC = () => {
  const [loading, setLoading] = useState<boolean>(false)
  const [data, setData] = useState<FilterDto[]>([])

  const loadData = (): void => {
    if (loading) return
    setLoading(true)
    mailboxApiHelper.filter
      .getAll()
      .then((res: any) => {
        const results = res.data != null && Array.isArray(res.data) ? res.data : []
        setData([...results])
        prevIdsRef.current = results.map((r: FilterDto) => r.id).join(',')
        setLoading(false)
      })
      .catch(() => {
        setLoading(false)
      })
  }

  const handleToggleEnabled = (item: FilterDto): void => {
    const newEnabled = !(item.enabled !== false)
    setData((prev) => prev.map((r) => (r.id === item.id ? { ...r, enabled: newEnabled } : r)))
    setLoading(true)
    mailboxApiHelper.filter
      .put(item.id, { enabled: newEnabled })
      .then(() => {
        loadData()
      })
      .catch((err: any) => {
        setData((prev) => prev.map((r) => (r.id === item.id ? { ...r, enabled: !newEnabled } : r)))
        setLoading(false)
        omMessage.error(err.message)
      })
  }

  const handleDelete = (item: FilterDto): void => {
    const ruleName = item.name || `Rule #${item.id}`

    confirmDelete({
      title: i18n.t('serverRules.deleteTitle'),
      content: i18n.t('serverRules.deleteConfirm', { name: ruleName }),
      width: 380,
      onOk: (closeFunc: () => void): void => {
        closeFunc()
        setLoading(true)
        mailboxApiHelper.filter
          .delete(item.id)
          .then(() => {
            loadData()
          })
          .catch((err: any) => {
            setLoading(false)
            omMessage.error(err.message)
          })
      },
    })
  }

  const prevIdsRef = useRef<string>('')

  const handleReorder = (newList: FilterDto[]): void => {
    const newIds = newList.map((r) => r.id).join(',')
    if (newIds === prevIdsRef.current) return
    prevIdsRef.current = newIds

    setData(newList)
    setLoading(true)
    mailboxApiHelper.filter
      .reorder(newList.map((r) => r.id))
      .then(() => {
        loadData()
      })
      .catch((err: any) => {
        setLoading(false)
        omMessage.error(err.message)
      })
  }

  useEffect(() => {
    loadData()
  }, [])

  const { user } = reduxStore.getState()
  const userName = user?.name || ''
  const userEmail = user?.email || ''

  return (
    <div>
      <div className={classNames(styles.header)}>
        <div className={classNames(styles.profile)}>
          <div className={classNames(styles.avatar)}>
            <Avatar {...({ size: 32, name: userName, email: userEmail, skipLogo: true } as any)} />
          </div>
          <div className={classNames(styles.userInfo)}>
            <div className={classNames(styles.userName)}>{userName}</div>
            <div className={classNames(styles.userEmail)}>{userEmail}</div>
          </div>
        </div>
        <OmButton
          className={classNames(styles.addNewRuleBtn)}
          onClick={() => openEditServerRule({ mode: 'add', onSave: loadData })}
        >
          + {i18n.t('serverRules.addNewRule')}
        </OmButton>
      </div>
      <div className={classNames(styles.desc)}>{i18n.t('serverRules.desc')}</div>
      <div className={classNames(styles.ruleList)}>
        <OmSpinner spinning={loading}>
          {data.length === 0 && !loading ? (
            <div className={classNames(styles.emptyText)}>{i18n.t('serverRules.noRules')}</div>
          ) : (
            <ReactSortable
              handle={`.${styles.drag}`}
              list={data as any}
              setList={handleReorder as any}
              animation={200}
            >
              {data.map((item) => (
                <div key={item.id} className={classNames(styles.ruleItem)}>
                  <div className={classNames(styles.ruleSwitch)}>
                    <OmSwitch
                      checked={item.enabled !== false}
                      onChange={() => handleToggleEnabled(item)}
                    />
                  </div>
                  <div className={classNames(styles.ruleContent)}>
                    <div className={classNames(styles.ruleName)}>
                      {item.name || `Rule #${item.id}`}
                    </div>
                    <div className={classNames(styles.ruleSummary)}>{buildSummary(item)}</div>
                  </div>
                  <div className={classNames(styles.edit)}>
                    <OmButton
                      variant="text"
                      onClick={() =>
                        openEditServerRule({
                          mode: 'edit',
                          filter: item,
                          userEmail: userEmail,
                          onSave: loadData,
                        })
                      }
                      icon={getMenuIcon('toolbar-edit', 'edit')}
                    />
                  </div>
                  <div className={classNames(styles.delete)}>
                    <OmButton
                      variant="text"
                      onClick={() => handleDelete(item)}
                      icon={getMenuIcon('toolbar-delete', 'delete')}
                    />
                  </div>
                  <div className={classNames(styles.drag)}>
                    <img src={dragHandleSvg} alt="drag" width={16} height={16} />
                  </div>
                </div>
              ))}
            </ReactSortable>
          )}
        </OmSpinner>
      </div>
    </div>
  )
}

interface ManageServerRulesOptions {
  title?: string
  afterClose?: () => void
}

export function openManageServerRules(options?: ManageServerRulesOptions): void {
  const opts = options || {}

  const tmpObj: any = {
    title: opts.title || i18n.t('serverRules.title'),
  }

  tmpObj.modalObj = confirmBase({
    wrapClassName: classNames(styles.manageServerRules),
    title: tmpObj.title,
    content: <ServerRulesList />,
    okText: i18n.t('common.button.close'),
    width: 640,
    maskClosable: false,
    okButtonProps: {
      className: classNames('type1', 'h38', 'w100'),
    },
    cancelButtonProps: {
      className: classNames('hidden'),
    },
    afterClose: () => {
      if (opts.afterClose != null) {
        opts.afterClose()
      }
    },
  })
}

const manageServerRulesPopup = {
  openManageServerRules,
}

export default manageServerRulesPopup
